import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Baker-lite RECENT · master-only queue view of every spec Baker-lite has drafted.
 * The Founder picks which scaffold to hand off to the full Baker.
 *
 * Founder ruling 2026-08-11 (Priority backlog · P0):
 *   "Baker-Lite Admin Queue — Build an admin view of recent Baker-lite scaffolds
 *   so the Founder can pick which to hand off to the full Baker."
 */
export default function BakerLiteRecentPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const token = localStorage.getItem("sage_master_token") || "";
      const r = await fetch(`${API}/api/sage/baker-lite/recent`, {
        headers: { "X-Sage-Master": token },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      setErr(String(e.message || e));
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handOff = async (scaffoldId) => {
    if (!confirm("Hand this spec to the full Baker? This burns Anthropic tokens.")) return;
    const token = localStorage.getItem("sage_master_token") || "";
    try {
      const r = await fetch(`${API}/api/sage/baker/bake/${scaffoldId}`, {
        method: "POST",
        headers: { "X-Sage-Master": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      // Route to /baker with the bake_id so the Founder can review
      window.location.href = `/baker?bake_id=${d.bake_id}&scaffold_id=${scaffoldId}`;
    } catch (e) {
      alert(`Bake failed: ${e.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Baker-lite · Master Queue</div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase mb-4" data-testid="baker-lite-recent-headline">
          The spec queue
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          Every draft spec Baker-lite has produced sits here.
          Pick one to hand off to the full Baker · the workshop turns spec into runnable code.
        </p>

        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={load}
            disabled={busy}
            data-testid="baker-lite-recent-refresh"
            className="border-2 border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-sage-accent hover:text-sage-bg disabled:opacity-50 transition-colors"
          >
            {busy ? "Loading…" : "Refresh"}
          </button>
          {data && (
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted" data-testid="baker-lite-recent-count">
              {data.count} scaffolds on the shelf
            </div>
          )}
        </div>

        {err && (
          <div className="mb-6 border border-sage-warn text-sage-warn p-4 text-sm" data-testid="baker-lite-recent-error">
            {err} · master token required · <Link to="/founder-login" className="underline">sign in</Link>
          </div>
        )}

        {data?.items?.length === 0 && !err && (
          <div className="border border-sage-border p-8 text-center text-sage-muted" data-testid="baker-lite-recent-empty">
            No scaffolds drafted yet. Kick one off at <Link to="/baker-lite" className="text-sage-accent underline">Baker-lite</Link>.
          </div>
        )}

        <div className="space-y-4" data-testid="baker-lite-recent-list">
          {data?.items?.map((it) => (
            <div key={it.scaffold_id} className="border border-sage-border p-5 hover:border-sage-accent transition-colors" data-testid={`baker-lite-scaffold-${it.scaffold_id}`}>
              <div className="flex justify-between items-start mb-3 gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-2xl font-bold uppercase tracking-tight text-sage-text">{it.spec?.name || "(unnamed)"}</div>
                  <div className="text-sm text-sage-muted sage-prose mt-1">{it.spec?.tagline}</div>
                </div>
                <div className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-sage-faint whitespace-nowrap">
                  {it.handed_off_to_baker ? (
                    <span className="border border-sage-accent text-sage-accent px-2 py-0.5">Handed to Baker</span>
                  ) : (
                    <span className="border border-sage-muted text-sage-muted px-2 py-0.5">Awaiting bake</span>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 text-xs text-sage-muted mb-3">
                <div><span className="text-sage-faint">Backend routes:</span> {(it.spec?.backend_routes || []).length}</div>
                <div><span className="text-sage-faint">Frontend pages:</span> {(it.spec?.frontend_pages || []).length}</div>
                <div><span className="text-sage-faint">Est. bake hours:</span> {it.spec?.estimated_bake_hours ?? "?"}</div>
              </div>

              <details className="mb-3">
                <summary className="cursor-pointer text-[10px] uppercase tracking-[0.15em] text-sage-accent">The pitch</summary>
                <div className="mt-2 text-sm text-sage-muted sage-prose whitespace-pre-wrap">{it.spec?.pitch}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.15em] text-sage-faint">Original idea</div>
                <div className="mt-1 text-xs text-sage-muted italic">{it.idea}</div>
              </details>

              <div className="flex gap-3">
                <button
                  onClick={() => handOff(it.scaffold_id)}
                  data-testid={`baker-hand-off-${it.scaffold_id}`}
                  className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text transition-colors"
                >
                  Hand to Baker →
                </button>
                <div className="text-[10px] text-sage-faint uppercase tracking-[0.15em] self-center">
                  filed {it.created_at?.slice(0, 16) || "?"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
